import { HemisphericLight, Vector3, Scene, Color3 } from "@babylonjs/core";
import { FloorConfig } from "./dungeon/FloorConfig.js";
import { FloorGenerator } from "./dungeon/FloorGenerator.js";
import { RoomManager } from "./dungeon/RoomManager.js";

/**
 * @typedef {Object} LevelCallbacks
 * @property {Function} [onLevelLoaded]
 * @property {Function} [onRoomCleared]
 * @property {Function} [onGameWon]
 */

/**
 * @class LevelManager
 * @description Orchestre la progression à travers les étages et les salles. Gère l'environnement, les portails et les transitions de niveau.
 */
export class LevelManager {
    /**
     * @param {import("../core/GameManager.js").GameContext} context
     * @param {LevelCallbacks} callbacks
     */
    constructor(context, callbacks = {}) {
        /** @type {import("../core/GameManager.js").GameContext} */
        this.context = context;
        /** @type {import("@babylonjs/core").Scene} */
        this.scene = context.scene;

        this.onLevelLoaded = callbacks.onLevelLoaded;
        this.onRoomCleared = callbacks.onRoomCleared;
        this.onGameWon = callbacks.onGameWon;

        /** @type {number} */
        this.currentFloor = 1;
        /** @type {number} */
        this.currentRoomIndex = 0;
        /** @type {import("./dungeon/FloorConfig.js").FloorData|null} */
        this.currentFloorConfig = null;
        /** @type {import("./dungeon/Room.js").Room|null} */
        this.currentRoom = null;

        /** @type {RoomManager} */
        this.roomManager = new RoomManager(this.scene);
        this.roomManager.setAudioManager(context.audioManager);

        /** @type {boolean} */
        this.roomClearedTriggered = false;

        /** @type {import("@babylonjs/core").Vector3|null} */
        this.exitPlatformLocation = null;
        /** @type {import("@babylonjs/core").Vector3|null} */
        this.bossExitPosition = null;

        /** @type {HemisphericLight|null} */
        this.light = null;

        /** @type {number} @private */
        this.spawnProtectionTime = 0;

        /** @type {Function|null} @private */
        this._snapCamera = null;
    }

    /**
     * Fait apparaître le joueur à une position spécifique ou au point de spawn de la salle.
     * @param {import("../entities/Player.js").Player} player
     * @param {Vector3} [position] - Position optionnelle (sinon utilise spawnPosition de la salle)
     */
    spawnPlayer(player, position = null) {
        let spawnPos;

        if (position) {
            spawnPos = position.clone();
        } else if (this.currentRoom && this.currentRoom.spawnPosition) {
            spawnPos = this.currentRoom.spawnPosition.clone();
        } else {
            spawnPos = new Vector3(0, 0.8, 0);
        }

        player.resetMovement();
        player.mesh.position.copyFrom(spawnPos);

        if (this._snapCamera) {
            this._snapCamera(spawnPos);
        }

        this.spawnProtectionTime = Date.now() + 500;
    }

    /**
     * Définit la fonction de rappel pour l'alignement de la caméra.
     * @param {Function} fn
     */
    setCameraSnap(fn) {
        this._snapCamera = fn;
    }

    /**
     * Initialise l'environnement global (lumière, brouillard).
     */
    initGlobalEnvironment() {
        this.light = new HemisphericLight("mainLight", new Vector3(0, 1, 0), this.scene);
        this.light.intensity = 0.5;
        this.scene.clearColor = new Color3(0.02, 0.02, 0.04).toColor4(1);
        this.scene.fogMode = Scene.FOGMODE_EXP;
        this.scene.fogColor = new Color3(0.01, 0.01, 0.02);
    }

    /**
     * Démarre un nouvel étage.
     * @param {number} floorNumber
     * @param {import("../entities/Player.js").Player} player
     * @param {Object} [aiData]
     */
    loadFloor(floorNumber, player, aiData = null) {
        this.currentFloor = floorNumber;
        this.currentRoomIndex = 0;
        this.currentFloorConfig = FloorConfig.getFloor(floorNumber);

        // ✅ Nouveau run : réinitialise tous les ordres de salles
        if (floorNumber === 1) {
            FloorGenerator.resetAllOrders();
        }

        if (this.light) {
            this.light.intensity = this.currentFloorConfig.theme.ambientIntensity;
        }
        this.scene.fogDensity = this.currentFloorConfig.theme.fogDensity;

        this.loadRoom(0, player, aiData);
    }

    /**
     * Charge une salle spécifique au sein de l'étage actuel.
     * @param {number} roomIndex
     * @param {import("../entities/Player.js").Player} player
     * @param {Object} [aiData]
     */
    loadRoom(roomIndex, player, aiData = null) {
        this.currentRoomIndex = roomIndex;
        this.currentFloorConfig = FloorConfig.getFloor(this.currentFloor);
        this.roomClearedTriggered = false;

        // ✅ Nettoyage de la salle précédente avant de charger la suivante
        if (this.currentRoom) {
            this.roomManager.clearRoom();
        }

        this.currentRoom = FloorGenerator.generateRoom(
            this.currentFloor,
            this.currentRoomIndex,
            this.currentFloorConfig.roomType,
            aiData
        );

        this.roomManager.loadRoom(this.currentRoom, this.currentFloorConfig);
        this.exitPlatformLocation = this.currentRoom.getExitPlatform();

        // Création immédiate du portail de sortie
        if (this.exitPlatformLocation) {
            this._createExitPortal(this.exitPlatformLocation, this.currentRoomIndex, this.currentFloorConfig);
        }

        // ✅ SPAWN PLAYER via la méthode centralisée
        this.spawnPlayer(player);

        // Déclenche le spawn via le callback du GameManager
        this._spawnEnemiesForRoom(this.currentRoom, this.currentFloorConfig);
    }

    /**
     * Crée le portail de sortie (vers la salle suivante ou l'étage suivant).
     * @param {Vector3} exitPlatform
     * @param {number} roomIndex
     * @param {import("./dungeon/FloorConfig.js").FloorData} config
     * @private
     */
    _createExitPortal(exitPlatform, roomIndex, config) {
        if (this.roomManager.exitTrigger) {
            this.roomManager.exitTrigger.dispose();
            this.roomManager.exitTrigger = null;
        }

        const isLastRoom = (roomIndex === config.rooms - 1);
        const pos = exitPlatform.position ? exitPlatform.position : exitPlatform;
        const playerSpawn = this.currentRoom ? this.currentRoom.spawnPosition : null;

        if (isLastRoom) {
            // Si on n'est PAS à l'étage du boss, portail d'étage classique
            if (!FloorConfig.isBossFloor(this.currentFloor)) {
                this.roomManager.createFloorPortal(pos);
            } else {
                // Si on est à l'étage du boss, on prépare la sortie finale
                this.bossExitPosition = pos;
            }
        } else {
            this.roomManager.createRoomPortal(pos, roomIndex + 1, playerSpawn);
        }
    }

    /**
     * Déclenche l'événement d'apparition des ennemis pour la salle.
     * @param {import("./dungeon/Room.js").Room} room
     * @param {import("./dungeon/FloorConfig.js").FloorData} floorConfig
     * @private
     */
    _spawnEnemiesForRoom(room, floorConfig) {
        if (this.onLevelLoaded) {
            this.onLevelLoaded(room.spawnPoints, room.enemyList ?? []);
        }
    }

    /**
     * Vérifie les interactions avec les portails de sortie.
     * @param {import("../entities/Player.js").Player} player
     * @param {import("./EntityManager.js").EntityManager} entityManager
     * @param {import("./ai/DataCollector.js").DataCollector} aiData
     */
    checkExitInteraction(player, entityManager, aiData) {
        if (Date.now() < this.spawnProtectionTime) return;

        if (this.checkPortalInteraction(player, entityManager, aiData)) return;

        if (this.roomManager.exitTrigger && player.mesh &&
            player.mesh.intersectsMesh(this.roomManager.exitTrigger, false)) {

            if (aiData && entityManager) {
                aiData.recordRoomCompletion(entityManager.getEnemyCount());
            }

            if (!FloorConfig.isBossFloor(this.currentFloor)) {
                // Récompense de fin d'étage : +4 HP max pour chaque étage normal complété
                if (player && typeof player.increaseMaxHealth === 'function') {
                    player.increaseMaxHealth(4);
                }
                this.loadFloor(this.currentFloor + 1, player, aiData);
            } else if (this.onGameWon) {
                this.onGameWon();
            }
        }
    }

    /**
     * Vérifie spécifiquement l'interaction avec les portails inter-salles.
     * @param {import("../entities/Player.js").Player} player
     * @param {import("./EntityManager.js").EntityManager} entityManager
     * @param {import("./ai/DataCollector.js").DataCollector} aiData
     * @returns {boolean} True si un portail a été franchi
     */
    checkPortalInteraction(player, entityManager, aiData) {
        if (Date.now() < this.spawnProtectionTime) return false;

        for (const portal of this.roomManager.portals) {
            if (portal.metadata && !portal.metadata.isLocked &&
                player.mesh && player.mesh.intersectsMesh(portal, false)) {

                this.applyGlitchEffect();

                if (aiData && entityManager) {
                    aiData.recordRoomCompletion(entityManager.getEnemyCount());
                }

                // Téléportation vers la salle suivante
                this.loadRoom(portal.metadata.nextRoomIndex, player, aiData);
                return true;
            }
        }
        return false;
    }

    /**
     * Gère la logique suite à l'élimination de tous les ennemis d'une salle.
     */
    onRoomEnemiesCleared() {
        if (this.roomClearedTriggered) return;
        this.roomClearedTriggered = true;

        if (this.currentFloor === 3 && this.currentRoomIndex === 2) {
            const bossPos = this.currentRoom.spawnPosition.clone();
            bossPos.y = 1;
            this.context.entityManager.spawnEnemy("SentinelleElite", bossPos);
            return;
        }

        if (this.currentFloor === 4 && this.currentRoomIndex === 2) {
            const bossPos = this.currentRoom.spawnPosition.clone();
            bossPos.y = 1;
            this.context.entityManager.spawnEnemy("Tank", bossPos);
            return;
        }

        if (this.onRoomCleared) this.onRoomCleared(this.currentRoomIndex);
    }

    /**
     * Vérifie les interactions avec les bonus au sol.
     * @param {import("../entities/Player.js").Player} player
     */
    checkBonusInteraction(player) {
        if (this.roomManager) this.roomManager.checkInteractions(player);
    }

    /**
     * Fait apparaître un bonus à une position donnée.
     * @param {Vector3} position
     * @param {string} enemyType
     */
    spawnBonusDrop(position, enemyType) {
        this.roomManager.spawnBonusDrop(position, enemyType);
    }

    /**
     * Déclenche l'apparition du portail final après la défaite du boss.
     */
    onBossDefeated() {
        if (this.bossExitPosition) {
            this.roomManager.createGrandPortal(this.bossExitPosition);
        }
    }

    /**
     * Applique un effet visuel de "glitch" lumineux.
     */
    applyGlitchEffect() {
        if (this.light) {
            const originalIntensity = this.currentFloorConfig ? this.currentFloorConfig.theme.ambientIntensity : 0.5;
            this.light.intensity = 2.0;
            setTimeout(() => { if (this.light) this.light.intensity = originalIntensity; }, 100);
        }
    }

    /**
     * Nettoie la salle actuelle.
     */
    clearCurrentRoom() {
        this.roomManager.clearRoom();
        this.currentRoom = null;
        this.exitPlatformLocation = null;
    }

    /**
     * @returns {Object|null}
     */
    getCurrentRoomData() {
        return this.currentRoom ? this.currentRoom.exportData() : null;
    }
}